import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
  Box,
  Button,
  Card,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { CompactTablePagination, EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { usePublicationCatalogPreview } from '../../hooks';
import { formatDateTime } from '../../lib/format';
import { PUBLICATION_CRITERIA_KIND_LABELS } from '../../model/options';
import type { PreviewProductResult } from '../../model/publicationTypes';

const PAGE_SIZE = 20;

function PreviewRow({ product }: { product: PreviewProductResult }) {
  const { t } = useTranslation('catalog');
  const winningExcludeRule = product.matchedExcludeRules[0];

  return (
    <TableRow hover>
      <TableCell>
        <Typography fontWeight={600}>{product.name}</Typography>
        <Typography variant="caption" color="text.secondary" component="code">
          {product.code}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          color={product.included ? 'success' : 'default'}
          variant={product.included ? 'filled' : 'outlined'}
          label={
            product.included
              ? t('publicationPreviewPanel.table.included')
              : t('publicationPreviewPanel.table.excluded')
          }
        />
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {!product.included && winningExcludeRule ? (
            <Tooltip title={t('publicationPreviewPanel.table.excludeRuleTooltip')}>
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={t(PUBLICATION_CRITERIA_KIND_LABELS[winningExcludeRule.criteriaKind])}
              />
            </Tooltip>
          ) : null}
          {product.missingRequiredFields.length > 0 ? (
            <Tooltip title={t('publicationPreviewPanel.table.missingFieldsTooltip')}>
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={t('publicationPreviewPanel.table.missingFieldsChip', {
                  fields: product.missingRequiredFields.join(', '),
                })}
              />
            </Tooltip>
          ) : null}
          {product.included && product.matchedIncludeRules.length > 0 ? (
            <Tooltip title={t('publicationPreviewPanel.table.includeRuleTooltip')}>
              <Chip
                size="small"
                variant="outlined"
                label={t('publicationPreviewPanel.table.matchedRulesCount', {
                  count: product.matchedIncludeRules.length,
                })}
              />
            </Tooltip>
          ) : null}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

/**
 * Dry-run membership preview. NOT server-paginated (`GET .../preview` returns the full result in
 * one call, no `page`/`size` params — see `CatalogPreviewResult`'s doc comment) — this panel
 * paginates the returned array client-side to keep the table usable for a large catalog.
 */
export function PublicationPreviewPanel({ catalogId }: { catalogId: string }) {
  const { t } = useTranslation('catalog');
  const previewQuery = usePublicationCatalogPreview(catalogId);
  const [page, setPage] = useState(0);

  if (previewQuery.isLoading) {
    return <LoadingState label={t('publicationPreviewPanel.loading')} />;
  }
  if (previewQuery.isError) {
    return <ErrorState message={previewQuery.error?.message} onRetry={() => void previewQuery.refetch()} />;
  }

  const products = previewQuery.data?.products ?? [];
  const pageItems = products.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {previewQuery.data
            ? t('publicationPreviewPanel.evaluatedAt', { date: formatDateTime(previewQuery.data.evaluatedAt) })
            : ''}
        </Typography>
        <Button
          startIcon={<RefreshRoundedIcon />}
          onClick={() => void previewQuery.refetch()}
          disabled={previewQuery.isFetching}
        >
          {t('publicationPreviewPanel.refresh')}
        </Button>
      </Stack>

      {products.length === 0 ? (
        <EmptyState
          title={t('publicationPreviewPanel.empty.title')}
          description={t('publicationPreviewPanel.empty.description')}
        />
      ) : (
        <Card variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('publicationPreviewPanel.table.product')}</TableCell>
                <TableCell>{t('publicationPreviewPanel.table.verdict')}</TableCell>
                <TableCell>{t('publicationPreviewPanel.table.reason')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageItems.map((product) => (
                <PreviewRow key={product.productId} product={product} />
              ))}
            </TableBody>
          </Table>
          <CompactTablePagination
            count={products.length}
            page={page}
            rowsPerPage={PAGE_SIZE}
            hideRowsPerPage
            onPageChange={(_e, next) => setPage(next)}
            onRowsPerPageChange={() => {}}
          />
        </Card>
      )}
    </Box>
  );
}
